import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Tag, Power } from "lucide-react";
import api from "../../lib/api";

const CATEGORIES = [
  { v: "sponsored_article", l: "Article sponsorisé", color: "var(--gai-red)" },
  { v: "banner", l: "Bannière digitale", color: "var(--gai-black)" },
  { v: "print_ad", l: "Encart print", color: "var(--gai-graphite)" },
  { v: "subscription", l: "Abonnement", color: "var(--gai-grey-700)" },
  { v: "event", l: "Événement", color: "var(--gai-red-dark)" },
  { v: "other", l: "Autre", color: "var(--gai-grey-500)" },
];

const UNITS = ["forfait", "parution", "mois", "an", "envoi", "événement", "jour"];

const eur = (n, c = "EUR") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n || 0);

function OfferForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    category: initial?.category || "sponsored_article",
    description: initial?.description || "",
    default_price: initial?.default_price ?? 0,
    currency: initial?.currency || "EUR",
    unit: initial?.unit || "forfait",
    active: initial?.active !== false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, default_price: parseFloat(form.default_price) || 0 };
      if (initial?.id) await api.patch(`/admin/offers/${initial.id}`, payload);
      else await api.post("/admin/offers", payload);
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
      <form onSubmit={submit} className="w-full max-w-2xl gai-card gai-sharp p-7 relative" data-testid="offer-form">
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 text-[var(--gai-grey-600)] hover:text-[var(--gai-gold)]" aria-label="Fermer">
          <X size={18} />
        </button>
        <div className="overline mb-1">{initial?.id ? "Modifier" : "Nouvelle"}</div>
        <h3 className="serif text-2xl text-[var(--gai-black)] mb-5">Offre commerciale</h3>

        <div className="space-y-4">
          <input required className="gai-input" placeholder="Nom de l'offre"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="offer-name" />
          <textarea rows={3} className="gai-input resize-none" placeholder="Description (visible client)"
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="offer-description" />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Catégorie</div>
              <select className="gai-input" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="offer-category">
                {CATEGORIES.map((c) => (
                  <option key={c.v} value={c.v} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{c.l}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="overline mb-1">Unité</div>
              <select className="gai-input" value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                {UNITS.map((u) => <option key={u} value={u} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{u}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1">Prix indicatif</div>
              <input type="number" min="0" step="1" required className="gai-input"
                value={form.default_price}
                onChange={(e) => setForm({ ...form, default_price: e.target.value })}
                data-testid="offer-price" />
            </div>
            <div>
              <div className="overline mb-1">Devise</div>
              <select className="gai-input" value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                {["EUR", "USD", "XOF", "XAF", "CDF", "MAD", "GBP"].map((c) =>
                  <option key={c} value={c} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{c}</option>)}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[var(--gai-grey-800)]">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <Power size={12} /> Active (visible au catalogue public)
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-5 mt-5 border-t" style={{ borderColor: "var(--gai-grey-200)" }}>
          <button type="button" onClick={onCancel} className="gai-btn-ghost text-xs">Annuler</button>
          <button disabled={busy} className="gai-btn-gold text-xs" data-testid="offer-submit">
            {busy ? "Enregistrement…" : (initial?.id ? "Enregistrer" : "Créer l'offre")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | offer
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    const r = await api.get("/admin/offers").catch(() => ({ data: [] }));
    setOffers(r.data || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const filtered = offers.filter((o) => filter === "all" || o.category === filter);
  const grouped = CATEGORIES.map((c) => ({ ...c, items: offers.filter((o) => o.category === c.v) }))
    .filter((g) => g.items.length > 0);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette offre du catalogue ?")) return;
    await api.delete(`/admin/offers/${id}`);
    refresh();
  };

  return (
    <div data-testid="page-admin-offers">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="overline mb-2">Inventaire commercial</div>
          <h1 className="serif text-4xl md:text-5xl text-[var(--gai-black)] leading-none">Catalogue d'offres</h1>
          <p className="mt-2 text-sm text-[var(--gai-grey-500)] max-w-2xl">
            Articles sponsorisés, bannières, encarts print, abonnements, événements — toute la grille tarifaire en un seul endroit, prête à être insérée dans un devis.
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="gai-btn-gold text-xs" data-testid="btn-new-offer">
          <Plus size={12} /> Nouvelle offre
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="offers-filters">
        <button onClick={() => setFilter("all")}
          className={`px-3 py-2 text-[0.7rem] tracking-[0.18em] uppercase border ${
            filter === "all"
              ? "bg-[var(--gai-red)] text-[var(--gai-white)] border-[var(--gai-red)]"
              : "border-[var(--gai-grey-200)] text-[var(--gai-black)] hover:border-[var(--gai-gold)]"
          }`}>
          Toutes <span className="ml-1 opacity-60">{offers.length}</span>
        </button>
        {CATEGORIES.map((c) => {
          const count = offers.filter((o) => o.category === c.v).length;
          if (!count) return null;
          return (
            <button key={c.v} onClick={() => setFilter(c.v)}
              className={`px-3 py-2 text-[0.7rem] tracking-[0.18em] uppercase border ${
                filter === c.v
                  ? "bg-[var(--gai-red)] text-[var(--gai-white)] border-[var(--gai-red)]"
                  : "border-[var(--gai-grey-200)] text-[var(--gai-black)] hover:border-[var(--gai-gold)]"
              }`}
              data-testid={`offers-filter-${c.v}`}>
              {c.l} <span className="ml-1 opacity-60">{count}</span>
            </button>
          );
        })}
      </div>

      {loading && <div className="text-sm text-[var(--gai-grey-500)]">Chargement…</div>}

      {!loading && offers.length === 0 && (
        <div className="gai-card gai-sharp p-10 text-center text-[var(--gai-grey-500)]">
          Aucune offre. Créez votre première offre du catalogue.
        </div>
      )}

      {/* Grouped by category when no filter, list when filtered */}
      {filter === "all" ? (
        <div className="space-y-10">
          {grouped.map((g) => (
            <section key={g.v} data-testid={`offers-group-${g.v}`}>
              <div className="flex items-center gap-2 mb-4 overline" style={{ color: g.color }}>
                <Tag size={12} /> {g.l} ({g.items.length})
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.items.map((o) => (
                  <OfferCard key={o.id} offer={o} category={g} onEdit={() => setEditing(o)} onDelete={() => remove(o.id)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((o) => {
            const cat = CATEGORIES.find((c) => c.v === o.category) || CATEGORIES[5];
            return <OfferCard key={o.id} offer={o} category={cat} onEdit={() => setEditing(o)} onDelete={() => remove(o.id)} />;
          })}
        </div>
      )}

      {editing && (
        <OfferForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function OfferCard({ offer, category, onEdit, onDelete }) {
  return (
    <div className="gai-card gai-sharp p-5 flex flex-col" data-testid={`offer-card-${offer.id}`}>
      <div className="flex items-start gap-3">
        <div className="overline shrink-0" style={{ color: category.color, opacity: offer.active ? 1 : 0.4 }}>
          ● {category.l}
        </div>
        {!offer.active && <span className="ml-auto text-[0.6rem] tracking-[0.18em] uppercase text-[var(--gai-grey-400)]">Inactive</span>}
      </div>
      <div className="serif text-lg text-[var(--gai-black)] mt-3 leading-tight">{offer.name}</div>
      {offer.description && (
        <p className="mt-2 text-xs text-[var(--gai-grey-600)] line-clamp-3 flex-1">{offer.description}</p>
      )}

      <div className="mt-4 pt-3 border-t flex items-end justify-between gap-2" style={{ borderColor: "var(--gai-grey-200)" }}>
        <div>
          <div className="serif text-2xl gai-gradient-gold leading-none">{eur(offer.default_price, offer.currency)}</div>
          <div className="text-[0.6rem] tracking-[0.18em] uppercase text-[var(--gai-grey-500)] mt-1">/ {offer.unit}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="gai-btn-ghost p-2" aria-label="Modifier" data-testid={`offer-edit-${offer.id}`}>
            <Pencil size={13} />
          </button>
          <button onClick={onDelete} className="gai-btn-ghost p-2 text-red-300 hover:text-red-200" aria-label="Supprimer" data-testid={`offer-delete-${offer.id}`}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
